/**
 * Schemas Zod compartilhados entre frontend e backend.
 *
 * Resolve C6 da auditoria: antes, Step4Sign.tsx enviava
 * { signatureMode, fullName, signDate, nationalId } mas o backend
 * lia { signatureMethod, fields, ownAccountDeclared, latitude, longitude }
 * — apenas signatureData batia em ambos. Campos digitados pelo signer
 * (nome completo, data) eram silenciosamente descartados.
 *
 * Agora frontend e backend usam o MESMO schema. Validação em ambos os
 * lados, sem mismatch possível.
 */

import { z } from 'zod'

export const SignatureMethod = z.enum(['drawn', 'typed', 'uploaded'])
export type SignatureMethod = z.infer<typeof SignatureMethod>

export const SignDocumentBody = z.object({
  /** Imagem base64 da assinatura (data:image/...;base64,...) */
  signatureData: z.string().min(20, 'Assinatura ausente ou muito curta'),

  /** Modo escolhido pelo signer */
  signatureMethod: SignatureMethod,

  /** Nome completo declarado pelo signer no momento da assinatura */
  fullName: z.string().min(1, 'Nome completo é obrigatório').max(200),

  /** Data da assinatura declarada (ISO 8601). Backend ainda usa signedAt = now(). */
  signDate: z.string().datetime().optional(),

  /** ID nacional (CPF/CNPJ/etc) re-confirmado pelo signer */
  nationalId: z.string().max(30).optional(),

  /** Signer confirma que o pagamento PIX foi feito de conta própria */
  ownAccountDeclared: z.boolean().optional().default(false),

  /** Geolocalização opcional */
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),

  /** Campos preenchidos pelo signer no PDF (NAME, DATE, NATIONAL_ID, etc) */
  fields: z
    .array(z.object({
      fieldId: z.string(),
      valor: z.string().max(500),
    }))
    .optional(),
})

export type SignDocumentBodyType = z.infer<typeof SignDocumentBody>
